import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ChatService, ChatContact, ChatPeer, ChatWindow } from '../../services/chat.service';
import { ListState, listStateOf } from '../list-state/list-state.component';

@Component({
  selector: 'app-chat-page',
  templateUrl: './chat-page.component.html',
  styleUrls: ['./chat-page.component.scss'],
})
export class ChatPageComponent implements OnInit, AfterViewInit, OnDestroy {
  public search = '';
  public lightboxUrl: string | null = null;
  /** Odległość od góry listy (px), przy której zaczynamy dociągać starsze wiadomości. */
  private readonly LOAD_OLDER_THRESHOLD = 60;

  @ViewChild('messagesEl') private messagesEl?: ElementRef<HTMLElement>;
  private msgSub?: Subscription;

  constructor(
    public chat: ChatService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    // Odśwież listę konwersacji z API. Po załadowaniu: wróć do aktywnej konwersacji
    // (ponowny wybór = markRead + zjazd na dół — activeConversation przeżywa
    // nawigację w serwisie, a świeży kontener DOM startuje od góry historii)
    // albo wybierz pierwszą z listy.
    this.chat.loadConversations(() => {
      const act = this.chat.activeConversation;
      if (act) {
        const c = this.chat.contacts.find(x => String(x.id) === String(act.peer.id));
        this.chat.selectConversation(c || (act.peer as any));
      } else {
        const newest = this.newestConversation();
        if (newest) { this.chat.selectConversation(newest); }
      }
    });

    // Scroll-management: historia/własna wiadomość → auto-dół; cudza → auto-dół
    // tylko przy dole, inaczej przycisk „przejdź do najnowszych" z licznikiem.
    this.msgSub = this.chat.messageAdded.subscribe(e => {
      if (e.win !== this.chat.activeConversation) { return; }
      if (e.initial || e.mine || this.isNearBottom()) {
        this.scrollToBottom();
        e.win.showJump = false;
        e.win.jumpCount = 0;
      } else {
        e.win.showJump = true;
        e.win.jumpCount = (e.win.jumpCount || 0) + 1;
      }
    });
  }

  ngAfterViewInit(): void {
    // Wejście na /chat z już aktywną konwersacją — pokaż od razu najnowsze,
    // nie czekając na odpowiedź loadConversations().
    if (this.active) { this.scrollToBottom(); }
  }

  ngOnDestroy(): void {
    if (this.msgSub) { this.msgSub.unsubscribe(); }
  }

  private isNearBottom(): boolean {
    const el = this.messagesEl && this.messagesEl.nativeElement;
    if (!el) { return true; }
    return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }

  private scrollToBottom(): void {
    const scroll = () => {
      const el = this.messagesEl && this.messagesEl.nativeElement;
      if (el) { el.scrollTop = el.scrollHeight; }
    };
    // setTimeout: poczekaj aż Angular dorenderuje nową wiadomość. Drugi przebieg
    // domyka przypadki, gdy fonty/obrazki podniosą scrollHeight tuż po renderze.
    setTimeout(scroll, 0);
    setTimeout(scroll, 150);
  }

  onScroll(): void {
    const win = this.active;
    if (win && win.showJump && this.isNearBottom()) {
      win.showJump = false;
      win.jumpCount = 0;
    }
    if (win) { this.maybeLoadOlder(win); }
  }

  /**
   * Przewinięcie pod sam początek listy dociąga starszą porcję historii (`ChatService.loadOlder`).
   * Bez tego widać było wyłącznie ostatnią porcję z pierwszego pobrania, a po odświeżeniu
   * strony starsza korespondencja była nieosiągalna.
   *
   * Zapamiętujemy odległość od DOŁU, nie `scrollTop`: to niezmiennik przy dopisywaniu treści
   * na górze, więc po doklejeniu wracamy dokładnie na tę samą wiadomość — niezależnie od tego,
   * czy przeglądarka sama poprawiła `scrollTop` (scroll anchoring) i ile miejsca zajął spinner.
   */
  private maybeLoadOlder(win: ChatWindow): void {
    const el = this.messagesEl && this.messagesEl.nativeElement;
    if (!el || el.scrollTop > this.LOAD_OLDER_THRESHOLD) { return; }
    const distFromBottom = el.scrollHeight - el.scrollTop;
    this.chat.loadOlder(win, () => {
      if (this.active !== win) { return; }   // użytkownik przełączył rozmowę w międzyczasie
      // setTimeout: jak w scrollToBottom — poczekaj, aż Angular dorenderuje nowe wiadomości.
      setTimeout(() => {
        const cur = this.messagesEl && this.messagesEl.nativeElement;
        if (cur) { cur.scrollTop = cur.scrollHeight - distFromBottom; }
      }, 0);
    });
  }

  jumpToLatest(): void {
    const win = this.active;
    if (win) { win.showJump = false; win.jumpCount = 0; }
    this.scrollToBottom();
  }

  onImageLoad(): void {
    // Obrazek dociągnął się po renderze — utrzymaj przyklejenie do dołu.
    if (this.isNearBottom()) { this.scrollToBottom(); }
  }

  /**
   * Rozmowa z ostatnią aktywnością. Wybierana po CZASIE, a nie „pierwsza z listy":
   * kolejność przychodzi z backendu i jest jego decyzją, a tutaj chodzi o konkretną
   * własność. Kontakty bez ani jednej wiadomości pomijamy — to lista kontaktów
   * doklejona przez `loadContacts()`, a nie otwarte rozmowy.
   */
  private newestConversation(): ChatContact | null {
    const rozmowy = this.chat.contacts.filter(c => c.lastMessage);
    if (!rozmowy.length) { return null; }

    return rozmowy.reduce((najnowsza, c) =>
      this.lastActivity(c) > this.lastActivity(najnowsza) ? c : najnowsza);
  }

  /** Znacznik ostatniej aktywności; brak albo niepoprawna data liczy się jako najstarsza. */
  private lastActivity(c: ChatContact): number {
    const t = c.time ? Date.parse(c.time) : NaN;
    return isNaN(t) ? 0 : t;
  }

  /**
   * Stan historii otwartej rozmowy. `ownEmpty`, bo rozmowa bez ani jednej wiadomości
   * nie potrzebuje komunikatu — puste okno z polem do pisania mówi to samo, tylko lepiej.
   */
  get messagesState(): ListState {
    const win = this.active;
    return listStateOf(!!win && !!win.loadingHistory, win ? win.messages : [], { ownEmpty: true });
  }

  /** Stan listy rozmów dla `<app-list-state>` — pustkę opisuje sam widok, niżej. */
  get contactsState(): ListState {
    return listStateOf(!this.chat.conversationsLoaded, this.chat.contacts, { ownEmpty: true });
  }

  /**
   * Czy lista jest pusta, bo nie ma ŻADNYCH rozmów — w odróżnieniu od pustego wyniku
   * wyszukiwania. Dotąd oba przypadki dostawały „Nie znaleziono użytkowników", więc
   * użytkownik bez ani jednej rozmowy czytał komunikat o nieudanym szukaniu.
   */
  get hasNoConversations(): boolean {
    return this.chat.conversationsLoaded && !this.chat.contacts.length;
  }

  get filteredContacts(): ChatContact[] {
    const q = (this.search || '').toLowerCase().trim();
    if (!q) { return this.chat.contacts; }
    return this.chat.contacts.filter(c => (c.name || '').toLowerCase().includes(q));
  }

  get active(): ChatWindow | null {
    return this.chat.activeConversation;
  }

  isActive(c: ChatContact): boolean {
    return !!this.active && String(this.active.peer.id) === String(c.id);
  }

  select(c: ChatContact): void {
    this.chat.selectConversation(c);
  }

  send(): void {
    if (this.active) { this.chat.send(this.active); }
  }

  onKey(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    } else if (this.active) {
      this.chat.notifyTyping(this.active);
    }
  }

  onPaste(event: ClipboardEvent): void {
    if (!this.active) { return; }
    const items = event.clipboardData && event.clipboardData.items;
    if (!items) { return; }
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file' && items[i].type.indexOf('image/') === 0) {
        event.preventDefault();
        const file = items[i].getAsFile();
        if (file) { this.readAndSend(file); }
        return;
      }
    }
  }

  onPickFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (file && this.active) { this.readAndSend(file); }
    input.value = '';
  }

  private readAndSend(file: File): void {
    if (!this.active) { return; }
    const win = this.active;
    // Podpis czytamy w momencie wyboru pliku (jak dotychczas) — konwersja jest
    // asynchroniczna i tekst dopisany w jej trakcie nie powinien zniknąć z pola.
    const caption = (win.draft || '').trim();
    win.error = undefined;
    // Walidacja i ewentualna konwersja w ChatService — wspólne z dokiem.
    this.chat.prepareAttachment(file).then(
      dataUrl => {
        this.chat.sendImage(win, dataUrl, caption);
        if ((win.draft || '').trim() === caption) { win.draft = ''; }
      },
      errorKey => { win.error = String(errorKey); }
    );
  }

  openImage(m: any): void {
    if (m && m.attachmentUrl && !m.pending && !m.error) { this.lightboxUrl = m.attachmentUrl; }
  }

  closeLightbox(): void {
    this.lightboxUrl = null;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeLightbox();
  }

  initials(name: string): string {
    if (!name) { return '?'; }
    return name.split(' ').filter(Boolean).map(p => p.charAt(0)).slice(0, 2).join('').toUpperCase();
  }

  time(ts: number): string {
    const d = new Date(ts);
    return this.hhmm(d);
  }

  /**
   * Czas ostatniej wiadomości w wierszu listy rozmów. `ChatContact.time` to surowy ISO —
   * albo prosto z backendu (`chat-api.service`: `time || updatedAt || updated_at`), albo
   * zapisany w tym samym formacie przez `ChatService.bumpConversation` — więc wiersz
   * pokazywał dosłownie „2026-08-04T10:58:14+00:00".
   *
   * Format jak w komunikatorach, w języku zalogowanego:
   *   dziś              → 10:58
   *   wczoraj           → Wczoraj 12:27
   *   ostatnie 7 dni    → wt 12:27
   *   starsze           → 31.07.2026
   *
   * Celowo natywne `toLocaleDateString`, a nie `DatePipe`: projekt nie rejestruje danych
   * locale Angulara (`registerLocaleData`), więc pipe i tak formatowałby po angielsku.
   * Wartości nieparsowalnej nie zjadamy — wracamy z oryginałem, żeby nic nie zniknęło.
   */
  listTime(value?: string): string {
    if (!value) { return ''; }
    const d = new Date(value);
    if (isNaN(d.getTime())) { return value; }

    // Różnica liczona na PÓŁNOCACH, nie na milisekundach: wiadomość sprzed 20 h może być
    // zarówno „dziś", jak i „wczoraj", a liczy się data w kalendarzu, nie odstęp.
    const days = Math.round((this.startOfDay(new Date()) - this.startOfDay(d)) / 86400000);

    // days < 0 = data z przyszłości (rozjechany zegar) — traktujemy jak dziś.
    if (days <= 0) { return this.hhmm(d); }
    if (days === 1) { return this.translate.instant('CHAT.YESTERDAY') + ' ' + this.hhmm(d); }
    if (days < 7) {
      const lang = this.translate.currentLang || this.translate.getDefaultLang() || undefined;
      return d.toLocaleDateString(lang, { weekday: 'short' }) + ' ' + this.hhmm(d);
    }
    return this.dmy(d);
  }

  /**
   * Pokazujemy OSTATNIĄ znaną informację. Wylogowanie zapisuje się tylko jawne, więc gdy
   * jest starsze od logowania albo go nie ma, ostatnim, co wiemy, jest samo logowanie.
   */
  sessionLabel(c: ChatPeer): string {
    const loggedOut = !c.online && this.ms(c.lastLogout) > this.ms(c.lastLogin);
    const stamp = loggedOut ? c.lastLogout : c.lastLogin;
    if (!stamp) { return ''; }

    const d = new Date(stamp);
    const when = isNaN(d.getTime()) ? stamp : this.dmy(d) + ' ' + this.hhmm(d);

    return this.translate.instant(loggedOut ? 'CHAT.LOGGED_OUT' : 'CHAT.LOGGED_IN', { when });
  }

  /** Znacznik czasu w ms; brak albo niepoprawna data liczy się jako najstarsza. */
  private ms(value?: string): number {
    const t = value ? Date.parse(value) : NaN;

    return isNaN(t) ? 0 : t;
  }

  private dmy(d: Date): string {
    return ('0' + d.getDate()).slice(-2) + '.' + ('0' + (d.getMonth() + 1)).slice(-2) + '.' + d.getFullYear();
  }

  private hhmm(d: Date): string {
    return ('0' + d.getHours()).slice(-2) + ':' + ('0' + d.getMinutes()).slice(-2);
  }

  private startOfDay(d: Date): number {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  }

  trackById(_i: number, c: ChatContact) {
    return c.id;
  }
}
